import { z } from 'zod';
import { MIME_FORMAT_CV, type FormatCv } from './format-cv.js';
import { normaliserTelephone } from './telephone.js';
import { VILLE_AUTRE, villeParCode } from './villes.js';
import { normaliserWhatsapp } from './whatsapp.js';

/**
 * Formulaire public de candidature (docs/FORMULAIRE_PUBLIC.md).
 *
 * Un seul objet technique : le **lien de formulaire**, porteur d'un jeton de
 * 8 caractères. Le poste n'est jamais demandé au candidat — il est porté par le
 * lien —, et la référence de l'annonce n'apparaît jamais dans l'URL.
 *
 * Ce module est la seule définition de ce qui est accepté : l'API le fait
 * respecter, la page publique s'en sert pour signaler les erreurs avant envoi.
 */

// ---------------------------------------------------------------------------
// Lien de formulaire
// ---------------------------------------------------------------------------

/** Base62, 8 caractères : ~2,2 × 10^14 combinaisons, indevinables à la main. */
export const LONGUEUR_JETON = 8;
const ALPHABET_JETON = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
export const MOTIF_JETON = /^[0-9A-Za-z]{8}$/;

export function jetonValide(valeur: string | null | undefined): boolean {
  return !!valeur && MOTIF_JETON.test(valeur);
}

/**
 * Tire un jeton depuis une source d'aléa **cryptographique** fournie par
 * l'appelant (`randomBytes` côté Node, `crypto.getRandomValues` côté
 * navigateur) : ce paquet ne dépend ni de l'un ni de l'autre.
 *
 * Le tirage rejette les octets qui déséquilibreraient l'alphabet — 62 ne
 * divise pas 256 — plutôt que de replier par un modulo qui rendrait les
 * premières lettres plus probables.
 */
export function tirerJeton(octets: (n: number) => Uint8Array): string {
  const limite = 256 - (256 % ALPHABET_JETON.length);
  let jeton = '';
  while (jeton.length < LONGUEUR_JETON) {
    for (const octet of octets(LONGUEUR_JETON)) {
      if (octet >= limite) continue;
      jeton += ALPHABET_JETON[octet % ALPHABET_JETON.length];
      if (jeton.length === LONGUEUR_JETON) break;
    }
  }
  return jeton;
}

/** `https://rh.tiba-invest.com/f/k7Q2xzP4` */
export function urlFormulaire(base: string, jeton: string): string {
  return `${base.replace(/\/+$/, '')}/f/${jeton}`;
}

// ---------------------------------------------------------------------------
// Canal de l'annonce
// ---------------------------------------------------------------------------

export const CANAUX_ANNONCE = ['plateformes', 'reseaux_sociaux'] as const;
export const canalAnnonceSchema = z.enum(CANAUX_ANNONCE);
export type CanalAnnonce = z.infer<typeof canalAnnonceSchema>;

export const LIBELLES_CANAL: Record<CanalAnnonce, string> = {
  plateformes: 'Plateformes',
  reseaux_sociaux: 'Réseaux sociaux',
};

// ---------------------------------------------------------------------------
// Soumissions
// ---------------------------------------------------------------------------

export const STATUTS_SOUMISSION = [
  'acceptee',
  'doublon',
  'rejetee_robot',
  'rejetee_debit',
  'erreur',
] as const;
export const statutSoumissionSchema = z.enum(STATUTS_SOUMISSION);
export type StatutSoumission = z.infer<typeof statutSoumissionSchema>;

export const LIBELLES_STATUT_SOUMISSION: Record<StatutSoumission, string> = {
  acceptee: 'Reçue',
  doublon: 'Déjà candidat',
  rejetee_robot: 'Rejetée (robot)',
  rejetee_debit: 'Rejetée (trop d’envois)',
  erreur: 'Erreur',
};

/**
 * Les trois statuts qui répondent `201 {etat: 'recu'}` au candidat : une
 * réponse identique n'apprend rien à un robot et ne révèle pas qu'une personne
 * est déjà en base.
 */
export const STATUTS_REPONSE_IDENTIQUE: readonly StatutSoumission[] = [
  'acceptee',
  'doublon',
  'rejetee_robot',
];

export const LANGUES_FORMULAIRE = ['fr', 'ar'] as const;
export const langueFormulaireSchema = z.enum(LANGUES_FORMULAIRE);
export type LangueFormulaire = z.infer<typeof langueFormulaireSchema>;

// ---------------------------------------------------------------------------
// Champs de la saisie
// ---------------------------------------------------------------------------

export const NOM_MIN = 2;
export const NOM_MAX = 80;
export const EXPERIENCE_MAX = 500;
export const VILLE_LIBRE_MAX = 80;

/** PDF, photo ou Word moderne. `.doc` n'est pas offert au public. */
export const FORMATS_CV_PUBLIC: readonly FormatCv[] = ['pdf', 'docx', 'jpg', 'png'];
export const ACCEPT_CV_PUBLIC = [
  ...FORMATS_CV_PUBLIC.map((f) => MIME_FORMAT_CV[f]),
  '.pdf',
  '.docx',
  '.jpg',
  '.jpeg',
  '.png',
].join(',');

/** Défaut du paramètre `formulaire.taille_max_cv_mo`, en octets. */
export const TAILLE_CV_PUBLIC_DEFAUT = 5 * 1024 * 1024;

/**
 * Champs que le formulaire ne demandera jamais (§ Interdits de CLAUDE.md, et
 * règle 10 de la spécification). La liste est vérifiée par un test : elle
 * empêche qu'un champ interdit se glisse un jour dans le schéma.
 */
export const CHAMPS_INTERDITS = [
  'age',
  'date_naissance',
  'dateNaissance',
  'sexe',
  'genre',
  'situation_familiale',
  'situationFamiliale',
  'enfants',
  'sante',
  'handicap',
  'photo',
  'cin',
  'nationalite',
  'religion',
] as const;

/**
 * Saisie du candidat. `.strict()` : un champ non prévu fait échouer la
 * validation plutôt que d'être ignoré en silence — c'est ce qui garantit
 * qu'aucune donnée interdite n'entre, même envoyée à la main.
 */
export const soumissionFormulaireSchema = z
  .object({
    nom: z.string().trim().min(NOM_MIN).max(NOM_MAX),
    telephone: z.string().trim().min(1),
    /** Vide quand la case « le même numéro que mon téléphone » est cochée. */
    whatsapp: z.string().trim().max(40).optional(),
    whatsappEstLeTelephone: z.boolean().default(false),
    email: z.string().trim().email().max(120).optional().or(z.literal('')),
    ville: z.string().trim().min(1),
    villeLibre: z.string().trim().max(VILLE_LIBRE_MAX).optional(),
    experience: z.string().trim().max(EXPERIENCE_MAX).optional(),
    langue: langueFormulaireSchema,
    /** Case d'information : refusée si elle n'est pas cochée. */
    consentement: z.literal(true),
    mentionVersion: z.string().trim().min(1).max(40),
    /** Horodatage signé rendu par le GET : sert à mesurer la durée de saisie. */
    jetonSaisie: z.string().trim().min(1),
    /** Champ piège, invisible : rempli, c'est un robot. */
    piege: z.string().max(200).optional(),
  })
  .strict()
  .superRefine((valeur, contexte) => {
    if (!normaliserTelephone(valeur.telephone)) {
      contexte.addIssue({ code: z.ZodIssueCode.custom, path: ['telephone'], message: 'telephone_invalide' });
    }
    if (!valeur.whatsappEstLeTelephone && valeur.whatsapp && !normaliserWhatsapp(valeur.whatsapp)) {
      contexte.addIssue({ code: z.ZodIssueCode.custom, path: ['whatsapp'], message: 'whatsapp_invalide' });
    }
    if (valeur.ville === VILLE_AUTRE) {
      if (!valeur.villeLibre) {
        contexte.addIssue({ code: z.ZodIssueCode.custom, path: ['villeLibre'], message: 'ville_requise' });
      }
    } else if (!villeParCode(valeur.ville)) {
      contexte.addIssue({ code: z.ZodIssueCode.custom, path: ['ville'], message: 'ville_inconnue' });
    }
  });

export type SoumissionFormulaire = z.infer<typeof soumissionFormulaireSchema>;

/**
 * Le champ piège est rempli, ou la saisie a duré moins que
 * `formulaire.duree_min_saisie_s` : c'est un robot. Le candidat reçoit la même
 * réponse que s'il avait été accepté ; seule la ligne en base dit la vérité.
 */
export function estRobot(
  saisie: { piege?: string | undefined },
  dureeSaisieMs: number,
  dureeMinSecondes: number,
): boolean {
  if (saisie.piege && saisie.piege.trim() !== '') return true;
  return dureeSaisieMs < dureeMinSecondes * 1000;
}

// ---------------------------------------------------------------------------
// Contrats d'API (docs/FORMULAIRE_PUBLIC.md § 4)
// ---------------------------------------------------------------------------

/**
 * Création d'une annonce « RS » : le strict nécessaire. Pas de texte long, pas
 * de critères, pas de publications — elle naît validée, avec sa référence, son
 * barème figé et son lien, en une seule transaction.
 */
export const creationAnnonceRsSchema = z
  .object({
    intitule: z.string().trim().min(3).max(120),
    familleId: z.string().uuid(),
    pointsDeVenteIds: z.array(z.string().uuid()).max(50).default([]),
  })
  .strict();
export type CreationAnnonceRs = z.infer<typeof creationAnnonceRsSchema>;

/**
 * « Candidature reçue par WhatsApp » (docs/FORMULAIRE_PUBLIC.md § 5 ter) :
 * saisie à la main, depuis la fiche annonce, après un échange sur le
 * WhatsApp Business de l'équipe. La réception reste **manuelle** dans ce lot —
 * l'application ne se branche sur aucun service Meta.
 *
 * Mêmes règles que partout : le téléphone est la clé de rapprochement, le
 * numéro WhatsApp n'en est jamais une, et une personne déjà candidate sur
 * l'annonce ne reçoit pas une seconde candidature (règle 8).
 */
export const candidatureWhatsappSchema = z
  .object({
    nom: z.string().trim().min(NOM_MIN).max(NOM_MAX),
    prenom: z.string().trim().max(NOM_MAX).optional(),
    telephone: z.string().trim().min(1),
    email: z.string().trim().email().max(120).optional(),
    ville: z.string().trim().max(VILLE_LIBRE_MAX).optional(),
    /** Vide quand la case « le même numéro que son téléphone » est cochée. */
    whatsapp: z.string().trim().max(40).optional(),
    whatsappEstLeTelephone: z.boolean().default(true),
  })
  .strict()
  .superRefine((valeur, contexte) => {
    if (!normaliserTelephone(valeur.telephone)) {
      contexte.addIssue({ code: z.ZodIssueCode.custom, path: ['telephone'], message: 'telephone_invalide' });
    }
    if (!valeur.whatsappEstLeTelephone && valeur.whatsapp && !normaliserWhatsapp(valeur.whatsapp)) {
      contexte.addIssue({ code: z.ZodIssueCode.custom, path: ['whatsapp'], message: 'whatsapp_invalide' });
    }
  });
export type CandidatureWhatsapp = z.infer<typeof candidatureWhatsappSchema>;

export interface ReponseCandidatureWhatsapp {
  candidatureId: string;
  /** Faux quand la personne était déjà candidate sur cette annonce (règle 8). */
  candidatureCreee: boolean;
  personneCreee: boolean;
}

/** `PATCH /liens-formulaire/:id` : on ne désactive, on ne réactive jamais. */
export const majLienFormulaireSchema = z.object({ actif: z.literal(false) }).strict();
export type MajLienFormulaire = z.infer<typeof majLienFormulaireSchema>;

/** Compteurs d'un lien, par statut de soumission. */
export interface CompteursLien {
  recues: number;
  doublons: number;
  rejetees: number;
}

export interface LienFormulaire {
  id: string;
  annonceId: string;
  jeton: string;
  url: string;
  /** `wa.me` pré-rempli, `null` si `whatsapp.numero_rh` n'est pas réglé. */
  urlWhatsapp: string | null;
  actif: boolean;
  creeLe: string;
  creePar: string | null;
  desactiveLe: string | null;
  desactivePar: string | null;
  compteurs: CompteursLien;
}

/** Réponse de `POST /annonces/rs` : l'annonce créée et son lien. */
export interface AnnonceRsCreee<T = unknown> {
  annonce: T;
  lien: LienFormulaire;
}

// ---------------------------------------------------------------------------
// Paramètres du lot (table `parametres`)
// ---------------------------------------------------------------------------

export const CLE_MENTION_VERSION = 'formulaire.mention_version';
export const CLE_NUMERO_WHATSAPP_RH = 'whatsapp.numero_rh';

/**
 * Version du texte d'information accepté par le candidat, gardée avec chaque
 * soumission : le texte peut changer, on doit pouvoir dire lequel a été
 * présenté. **À figer une fois le texte validé par le gérant** (§ 12).
 */
export const MENTION_VERSION_DEFAUT = '2026-09-20';

// ---------------------------------------------------------------------------
// Traitement différé (worker)
// ---------------------------------------------------------------------------

/**
 * File du traitement d'une soumission acceptée : personne, candidature,
 * document, extraction, adéquation. L'API n'attend pas — le candidat reçoit sa
 * confirmation avant que l'OCR ne commence.
 */
export const FILE_FORMULAIRE = 'formulaire';

export interface TravailFormulaire {
  soumissionId: string;
}

/**
 * Un travail par soumission (`jobId`), pour qu'un renvoi du même envoi ne crée
 * pas deux candidatures. Trois tentatives espacées : une panne de base passagère
 * ne doit pas perdre une candidature.
 */
export const jobIdFormulaire = (soumissionId: string) => `formulaire-${soumissionId}`;
export const OPTIONS_TRAVAIL_FORMULAIRE = {
  attempts: 3,
  backoff: { type: 'fixed', delay: 30_000 },
  removeOnComplete: 500,
  removeOnFail: 500,
} as const;

/** Réponse rendue au candidat : la même pour une acceptation, un doublon, un robot. */
export interface ReponseSoumission {
  etat: 'recu';
}

/** Ce que la page publique lit avant d'afficher le formulaire. */
export interface FormulairePublic {
  titre: string;
  pointsDeVente: string[];
  etat: 'ouvert' | 'clos';
  /** Horodatage signé : sert à mesurer la durée de saisie. Absent si clos. */
  jetonSaisie: string | null;
  mentionVersion: string;
  /** `wa.me` pré-rempli, `null` si `whatsapp.numero_rh` n'est pas réglé. */
  urlWhatsapp: string | null;
}
